// «No leído» en la oficina (30-09, Pau: «la notificación de no leído debería ser única y exclusivamente cuando
// tenga que hablar yo»). Es EL mismo indicador del resto del panel (barra lateral, campana, Telegram): lo decide
// el servidor (`noLeido` del contratado) y se enciende solo cuando el agente le devuelve la palabra o le pide
// algo; nunca mientras trabaja y narra. Antes la oficina llevaba su propia cuenta en el navegador (cualquier
// frase del agente encendía el punto, y el móvil y el ordenador no coincidían). Ver docs/oficina-v2/no-leido.md.
import { useEffect, useRef } from 'react';
import { useAppStore } from '../../stores/app.store';
import type { ContratadoDTO } from './compartido/vocabulario';
import { bandejaDe, miBandeja } from './marco/bandejas';
import { useOficinaStore } from './store';

/** ¿Le toca a Pau con este agente y aún no lo ha leído? */
export function useNoLeido(c: Pick<ContratadoDTO, 'noLeido'>): boolean {
  return c.noLeido;
}

/**
 * Mientras el chat de un agente está abierto y su dueño está mirando esa ventana, lo que le toca queda leído (en el
 * servidor: se apaga también en la barra lateral y en los demás dispositivos). Si la pestaña está oculta o la
 * ventana no tiene el foco (un ordenador olvidado con el chat abierto), espera a que vuelva: si no, se comería
 * los avisos al móvil.
 */
export function useMarcarLeido(c: Pick<ContratadoDTO, 'agentId' | 'noLeido' | 'dueno'> | undefined): void {
  const markRead = useAppStore((s) => s.markRead);
  const yo = useOficinaStore((s) => s.yo);
  const pedido = useRef<string | null>(null);
  const agentId = c?.agentId;
  // 06-10 — solo su dueño lo marca leído: si Pau mira la bandeja de Carlos, a Carlos le sigue saliendo «no leído».
  // Mientras no se sabe quién mira, no se marca nada.
  const noLeido = !!c?.noLeido && yo !== null && bandejaDe(c) === miBandeja(yo);
  useEffect(() => {
    if (!agentId || !noLeido) {
      pedido.current = null;
      return;
    }
    const marcar = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus() || pedido.current === agentId) return;
      pedido.current = agentId;
      markRead(agentId);
    };
    marcar();
    document.addEventListener('visibilitychange', marcar);
    window.addEventListener('focus', marcar);
    return () => {
      document.removeEventListener('visibilitychange', marcar);
      window.removeEventListener('focus', marcar);
    };
  }, [agentId, noLeido, markRead]);
}
